/**
 * productConfigSlice — per-(cartItem, flow) serialized config-flow output.
 *
 * Keyed by `"${cartItemId}|${flowId}"` (composite ConfigKey). Adapters (Phase 3)
 * own the concrete DTO shape and call `setConfig`/`clearConfig` on completion.
 * Serialize/hydrate happens on the draftSlice; this slice just holds the JSON.
 *
 * Phase 1.6 — changed from `Record<CartItemId, ConfigOutputDTO>` to
 * `Record<ConfigKey, ConfigOutputDTO>` so multi-flow products (e.g. a bundle
 * requiring measurement + floor-plan) each carry an independent output DTO
 * instead of one clobbering the other. `pfVersion` bumped to 2.
 *
 * Phase C-A: also tracks `cartItemUserConfirmed` — a per-(cartItem, flow)
 * boolean flag set when a complete tab is shown to the client (`useAutoConfirmFlow`). The
 * chip-strip checkmark + step-level Proceed gating both read this flag,
 * not bare `productConfigs` presence (which lights green on the first commit
 * even before the user has reviewed). Adapters call `markConfigDirty` on
 * every edit; the slice ignores no-op transitions.
 */

import type { StateCreator } from 'zustand'

import type { CartItemId, ConfigKey, ConfigOutputDTO, ConfigurationFlowId } from 'models/purchaseFlowRevamp'
import type { StagingDraftDTO } from '../../modules/staging/staging.dto'
import { sweepOrphanedStagingRooms } from '../../modules/staging/staging.orphans'
import { FLOW_IS_COMPLETE } from '../../modules/completeness'
import type { PfStoreState } from '../store'

/** Canonical composite key format: `"${cartItemId}|${flowId}"`. */
export const makeConfigKey = (cartItemId: CartItemId, flowId: ConfigurationFlowId): ConfigKey =>
  `${cartItemId}|${flowId}`

/** Key suffix of every staging config entry (`makeConfigKey(ci, 'staging')`). */
const STAGING_KEY_SUFFIX = '|staging'

export interface ProductConfigSliceState {
  /** Configs keyed by `"${cartItemId}|${flowId}"` (phase 1.6 composite key). */
  productConfigs: Record<ConfigKey, ConfigOutputDTO>
  /**
   * Per-(cartItem, flow) "tab confirmed" flag. Drives the
   * chip-strip filled-checkmark badge and gates the step-level Proceed CTA.
   * Resets to false on first edit after save (see `markConfigDirty`).
   *
   * Legacy drafts (pre-Phase C-A) deserialize to `{}` — the user is asked to
   * click Save once after resuming. No back-fill heuristic per the design
   * spec's open-question resolution #3.
   */
  cartItemUserConfirmed: Record<CartItemId, Partial<Record<ConfigurationFlowId, boolean>>>
  /**
   * Per-(cartItem, flow) "the client has opened this page" flag, for a
   * configurator whose pages are flows of their own (the authorities document's
   * power of attorney + property information).
   *
   * Session-only — deliberately NOT serialized into the draft. A resumed draft
   * that was already saved carries `cartItemUserConfirmed`, which is what the
   * Proceed gate reads; an unsaved one legitimately asks the client to look at
   * the page again.
   */
  configPagesVisited: Record<CartItemId, Partial<Record<ConfigurationFlowId, boolean>>>
}

export interface ProductConfigSliceActions {
  /** Write a serialized DTO for a (cartItem, flow) pair. */
  setConfig(cartItemId: CartItemId, flowId: ConfigurationFlowId, dto: ConfigOutputDTO): void
  /** Clear all config entries for `cartItemId` across all flows. */
  clearConfig(cartItemId: CartItemId): void
  /** The active (cartItem, flow) tab is complete and was seen by the client. */
  markConfigSaved(cartItemId: CartItemId, flowId: ConfigurationFlowId): void
  /** The client has opened this configurator page. Idempotent. */
  markConfigPageVisited(cartItemId: CartItemId, flowId: ConfigurationFlowId): void
  /** Module ops-hook calls this on user edit. No-op when already false; safe to
   *  call on every keystroke. */
  markConfigDirty(cartItemId: CartItemId, flowId: ConfigurationFlowId): void
  /**
   * `setConfig` + `markConfigDirty`, but the un-confirm happens ONLY when the DTO actually changed.
   *
   * The two calls were made as a pair by every module's `patch`, and `setConfig` is
   * JSON-eq-guarded while `markConfigDirty` is value-blind — so re-picking the value that is already
   * selected wrote nothing and still cleared the Saved state. Modules had started working around it
   * with their own early-returns (see `useStagingConfig`'s value-guard note); this makes the store
   * the one place that knows, so every module inherits the fix instead of re-deriving it.
   */
  patchConfig(cartItemId: CartItemId, flowId: ConfigurationFlowId, dto: ConfigOutputDTO): void
  /**
   * W-01 (P0) — drops staging rooms whose source photos were uploaded under a
   * purchase session other than `currentSessionId`, across every staging config
   * entry. Returns the number of rooms dropped so the caller can surface a
   * count toast (mirrors `cartSlice.clearStaleItems` + `drafts.resume_stale_items`).
   *
   * Called from the draft-hydration path (`store/rehydrate.ts`) ONLY — never
   * from a component mount effect (MODULE-CONTRACT §11.1). Detection lives in
   * the pure `modules/staging/staging.orphans.ts`.
   *
   * Un-confirms the affected `(cartItem, staging)` pairs: the saved config no
   * longer describes what the user confirmed, so Proceed must re-gate on an
   * explicit Save (same treatment as any post-Save edit, `markConfigDirty`).
   * Flags `draft.dirty` so the cleaned-up draft is autosaved.
   *
   * Also drops the rooms of `droppedFiles`, the uploads the resume found expired: a room without its
   * photo still reads as configured, and the line would be ordered without photos.
   */
  dropOrphanedStagingRooms(currentSessionId: string | null | undefined, droppedFiles?: ReadonlySet<string>): number
}

export type ProductConfigSlice = ProductConfigSliceState & ProductConfigSliceActions

export const createProductConfigSlice: StateCreator<PfStoreState, [], [], ProductConfigSlice> = (set, get) => ({
  productConfigs: {},
  cartItemUserConfirmed: {},

  configPagesVisited: {},

  setConfig: (cartItemId, flowId, dto) => set((state) => {
    const key = makeConfigKey(cartItemId, flowId)
    const existing = state.productConfigs[key]
    // Idempotency guard: skip if the structural DTO is identical to the existing
    // entry. Without this, every call (including StrictMode-induced double-fires
    // and exit-useEffect serializes) created a new productConfigs reference,
    // re-rendering every productConfigs subscriber and producing a commit-phase
    // cascade with the ref-callback cleanup path that wedged 3-item carts.
    if (existing != null && JSON.stringify(existing) === JSON.stringify(dto)) {
      return state
    }
    // A confirmed tab left incomplete by this write is confirmed no longer, or Proceed would open on it.
    const confirmed = state.cartItemUserConfirmed[cartItemId]
    const isComplete = FLOW_IS_COMPLETE[flowId]
    const losesConfirmation = confirmed?.[flowId] === true && !!isComplete && !isComplete(dto)
    return {
      productConfigs: { ...state.productConfigs, [key]: dto },
      draft: { ...state.draft, dirty: true },
      ...(losesConfirmation
        ? { cartItemUserConfirmed: { ...state.cartItemUserConfirmed, [cartItemId]: { ...confirmed, [flowId]: false } } }
        : {}),
    }
  }),

  clearConfig: (cartItemId) => set((state) => {
    // Remove all entries whose key starts with `${cartItemId}|`.
    const prefix = `${cartItemId}|`
    const hasAny = Object.keys(state.productConfigs).some((k) => k.startsWith(prefix))
    if (!hasAny) return state
    const rest: Record<ConfigKey, ConfigOutputDTO> = {}
    for (const [k, v] of Object.entries(state.productConfigs)) {
      if (!k.startsWith(prefix)) rest[k] = v
    }
    const { [cartItemId]: _removedConfirmed, ...restConfirmed } = state.cartItemUserConfirmed
    const { [cartItemId]: _removedVisited, ...restVisited } = state.configPagesVisited
    return {
      productConfigs: rest,
      cartItemUserConfirmed: restConfirmed,
      configPagesVisited: restVisited,
      draft: { ...state.draft, dirty: true },
    }
  }),

  markConfigSaved: (cartItemId, flowId) => set((state) => {
    const existing = state.cartItemUserConfirmed[cartItemId] ?? {}
    if (existing[flowId] === true) return state
    return {
      cartItemUserConfirmed: {
        ...state.cartItemUserConfirmed,
        [cartItemId]: { ...existing, [flowId]: true },
      },
      draft: { ...state.draft, dirty: true },
    }
  }),

  markConfigPageVisited: (cartItemId, flowId) => set((state) => {
    const existing = state.configPagesVisited[cartItemId] ?? {}
    if (existing[flowId] === true) return state
    return {
      configPagesVisited: {
        ...state.configPagesVisited,
        [cartItemId]: { ...existing, [flowId]: true },
      },
    }
  }),

  patchConfig: (cartItemId, flowId, dto) => set((state) => {
    const key = makeConfigKey(cartItemId, flowId)
    const existing = state.productConfigs[key]
    // The one comparison, shared by the write and the un-confirm — they cannot disagree.
    if (existing != null && JSON.stringify(existing) === JSON.stringify(dto)) return state

    const confirmed = state.cartItemUserConfirmed[cartItemId]
    const wasConfirmed = confirmed?.[flowId] === true

    return {
      productConfigs: { ...state.productConfigs, [key]: dto },
      draft: { ...state.draft, dirty: true },
      ...(wasConfirmed
        ? { cartItemUserConfirmed: { ...state.cartItemUserConfirmed, [cartItemId]: { ...confirmed, [flowId]: false } } }
        : {}),
    }
  }),

  markConfigDirty: (cartItemId, flowId) => set((state) => {
    const existing = state.cartItemUserConfirmed[cartItemId]
    if (!existing || existing[flowId] !== true) return state
    return {
      cartItemUserConfirmed: {
        ...state.cartItemUserConfirmed,
        [cartItemId]: { ...existing, [flowId]: false },
      },
      draft: { ...state.draft, dirty: true },
    }
  }),

  dropOrphanedStagingRooms: (currentSessionId, droppedFiles) => {
    // Two-phase (compute over `get()`, then one `set`) so the returned count is
    // never coupled to how many times a Zustand updater is invoked.
    const state = get()

    const nextConfigs: Record<ConfigKey, ConfigOutputDTO> = { ...state.productConfigs }
    const nextConfirmed = { ...state.cartItemUserConfirmed }
    let droppedRooms = 0

    for (const [key, dto] of Object.entries(state.productConfigs)) {
      if (!key.endsWith(STAGING_KEY_SUFFIX)) continue

      const cartItemId = key.slice(0, -STAGING_KEY_SUFFIX.length)
      const { dto: sweptDto, droppedKeys } = sweepOrphanedStagingRooms(
        dto as unknown as StagingDraftDTO | undefined,
        currentSessionId,
        droppedFiles
      )
      if (droppedKeys.length === 0) continue

      droppedRooms += droppedKeys.length
      nextConfigs[key] = sweptDto as unknown as ConfigOutputDTO

      if (nextConfirmed[cartItemId]?.staging === true) {
        nextConfirmed[cartItemId] = { ...nextConfirmed[cartItemId], staging: false }
      }
    }

    if (droppedRooms === 0) return 0

    set((current) => ({
      productConfigs: nextConfigs,
      cartItemUserConfirmed: nextConfirmed,
      draft: { ...current.draft, dirty: true },
    }))

    return droppedRooms
  },
})
